import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";

// F13 — explicit, user-authored room assignment for during-tenancy evidence (Monthly Checkup /
// Maintenance). Never guesses a room. Organizational metadata only — does not touch evidence bytes.
export function RoomAssign({ propertyId, evidenceType, evidenceId, assignedRoom, disabled, onChanged }) {
  const [open, setOpen] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [sel, setSel] = useState(assignedRoom?.room_id || "");
  const [saving, setSaving] = useState(false);
  const base = evidenceType === "monthly_checkup" ? "snapshots" : "maintenance";

  useEffect(() => {
    if (!open || rooms.length) return;
    api.get(`/properties/${propertyId}`)
      .then(({ data }) => setRooms((data.rooms || []).filter((r) => !r.is_deleted)))
      .catch(() => {});
  }, [open, propertyId, rooms.length]);

  const save = async (roomId) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/${base}/${evidenceId}/room`, { room_id: roomId });
      onChanged && onChanged(data.assigned_room);
      setOpen(false);
      toast.success(roomId ? "Room confirmed by you" : "Room assignment removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update the room.");
    } finally { setSaving(false); }
  };

  if (disabled) {
    return (
      <p className="mt-1.5 text-[11px] text-white/45 inline-flex items-center gap-1" data-testid={`roomassign-readonly-${evidenceId}`}>
        <MapPin size={11} /> {assignedRoom ? `Room: ${assignedRoom.name} (read-only)` : "Not assigned to a room (read-only)"}
      </p>
    );
  }

  if (!open) {
    return (
      <button data-testid={`roomassign-btn-${evidenceId}`} onClick={() => setOpen(true)}
        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-white/70 hover:text-white border border-white/15 bg-white/5 rounded-full px-2 py-1">
        <MapPin size={11} />
        {assignedRoom
          ? <span data-testid={`roomassign-current-${evidenceId}`}>Room confirmed by you: {assignedRoom.name}</span>
          : <span data-testid={`roomassign-choose-${evidenceId}`}>Choose a room</span>}
      </button>
    );
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`roomassign-panel-${evidenceId}`}>
      <select data-testid={`roomassign-select-${evidenceId}`} value={sel} onChange={(e) => setSel(e.target.value)}
        className="h-8 rounded-lg bg-white/10 border border-white/20 px-2 text-xs text-white max-w-[160px]">
        <option value="">Select a room…</option>
        {rooms.map((r) => <option key={r.room_id} value={r.room_id} className="text-black">{r.name}</option>)}
      </select>
      <button data-testid={`roomassign-save-${evidenceId}`} disabled={saving || !sel} onClick={() => save(sel)}
        className="h-8 px-2 rounded-lg bg-brand hover:bg-brand-hover text-white text-xs inline-flex items-center gap-1 disabled:opacity-40">
        {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Confirm
      </button>
      {assignedRoom && (
        <button data-testid={`roomassign-remove-${evidenceId}`} disabled={saving} onClick={() => save(null)}
          className="h-8 px-2 rounded-lg border border-white/15 text-white/70 hover:text-white text-xs">Remove</button>
      )}
      <button data-testid={`roomassign-cancel-${evidenceId}`} onClick={() => setOpen(false)}
        className="h-8 w-8 grid place-items-center rounded-lg text-white/50 hover:text-white"><X size={13} /></button>
    </div>
  );
}
